// pages/TeamsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
  Grid,
  Skeleton,
} from "@mui/material";
import Groups2Icon from "@mui/icons-material/Groups2";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PublicIcon from "@mui/icons-material/Public";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import PageShell from "../components/PageShell";
import ResultHistory from "../components/ResultHistory";
import { ACC_FILES, AC_FILES } from "../results/files.config";
import { getTeamImage, useTeamImageFallback } from "../utils/teamImages";

const slugifyTeamName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeTeamName = (value) => String(value || "").trim();

const msToLap = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0 || ms === 2147483647 || ms === 999999999) return "—";
  const total = Math.floor(ms);
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const rem = total % 1000;
  return `${m}:${s.toString().padStart(2, "0")}.${rem.toString().padStart(3, "0")}`;
};

const __jsonPromiseCache = new Map();

async function fetchJSONUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      console.warn("File not found:", url, res.status);
      return null;
    }

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    const isGzip = u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if (isGzip) {
      try {
        if (typeof DecompressionStream === "function") {
          const ds = new DecompressionStream("gzip");
          const stream = new Response(buf).body.pipeThrough(ds);
          buf = await new Response(stream).arrayBuffer();
          u8 = new Uint8Array(buf);
        }
      } catch (e) {
        console.error("Gzip decode failed:", url, e);
        return null;
      }
    }

    const tryDecode = (enc) => {
      try {
        return JSON.parse(new TextDecoder(enc, { fatal: false }).decode(u8));
      } catch {
        return null;
      }
    };

    return tryDecode("utf-8") || tryDecode("utf-16le") || tryDecode("utf-16be");
  } catch (e) {
    console.error("fetchJSON failed:", url, e);
    return null;
  }
}

async function fetchJSON(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJSONUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

const labelSession = (raw) => {
  const v = String(raw || "").toUpperCase();
  if (["FP", "P", "PRACTICE"].includes(v)) return "Practice";
  if (["Q", "QUALIFY", "QUALI", "QUALIFYING"].includes(v)) return "Qualifying";
  if (["R", "RACE"].includes(v)) return "Race";
  return raw || "—";
};

const cleanId = (v) => {
  if (!v) return "";
  const str = String(v).trim();
  return str.replace(/^steam:/i, "").replace(/^S/i, "");
};

const idsEqual = (a, b) => cleanId(a) === cleanId(b);

function parseACC(json, driverId) {
  const lines = json?.sessionResult?.leaderBoardLines ?? [];
  const driverIdClean = cleanId(driverId);

  const idx = lines.findIndex((l) => {
    const cur = l?.currentDriver?.playerId;
    const d0 = l?.car?.drivers?.[0]?.playerId;
    return idsEqual(cur, driverIdClean) || idsEqual(d0, driverIdClean);
  });
  if (idx < 0) return null;

  const hit = lines[idx];
  const best = hit?.timing?.bestLap ?? null;
  const lapCount = hit?.timing?.lapCount ?? null;
  const sessionName = labelSession(json?.sessionType ?? json?.Type);

  return {
    sim: "ACC",
    session: sessionName,
    best,
    laps: Number.isFinite(lapCount) ? lapCount : null,
    position: sessionName === "Race" ? idx + 1 : null,
  };
}

function parseAC(json, driverId) {
  const guid = cleanId(driverId);
  const laps = Array.isArray(json?.Laps) ? json.Laps : [];
  const results = Array.isArray(json?.Result) ? json.Result : [];
  const cars = Array.isArray(json?.Cars) ? json.Cars : [];

  const byLap = laps.filter((l) => cleanId(l?.DriverGuid ?? l?.Driver?.Guid) === guid);
  const inResultsIdx = results.findIndex((r) => cleanId(r?.DriverGuid) === guid);
  const inResults = inResultsIdx >= 0;
  const inCars = cars.some((c) => cleanId(c?.Driver?.Guid) === guid);

  if (!byLap.length && !inResults && !inCars) return null;

  let best = null;
  for (const l of byLap) {
    const t = l?.LapTime;
    if (Number.isFinite(t) && t > 0 && t !== 999999999) {
      best = best === null ? t : Math.min(best, t);
    }
  }

  const sessionName = labelSession(json?.Type);

  return {
    sim: "AC",
    session: sessionName,
    best,
    laps: byLap.length || null,
    position: sessionName === "Race" && inResults ? inResultsIdx + 1 : null,
  };
}

function computeRaceStatsFromRows(rows) {
  const raceRows = rows.filter((r) => (r.session || "").toLowerCase() === "race");

  const finishes = raceRows.length;
  const podiums = raceRows.reduce(
    (acc, r) => (Number.isFinite(r?.position) && r.position <= 3 ? acc + 1 : acc),
    0
  );
  const wins = raceRows.reduce((acc, r) => (r.position === 1 ? acc + 1 : acc), 0);
  const positions = raceRows.map((r) => r?.position).filter((v) => Number.isFinite(v));
  const bestPos = positions.length ? Math.min(...positions) : null;
  const avgPos = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : null;
  const totalRaceLaps = raceRows.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);

  return {
    finishes,
    podiums,
    wins,
    bestPos,
    avgPos,
    totalRaceLaps,
  };
}

function bestOfRows(rows) {
  let best = null;

  for (const r of rows) {
    if (
      Number.isFinite(r?.best) &&
      r.best > 0 &&
      r.best !== 999999999 &&
      r.best !== 2147483647
    ) {
      if (best === null || r.best < best) {
        best = r.best;
      }
    }
  }

  return best;
}

function getACCDateString(f) {
  const fullDateList = f.split("/")[1].split("_");
  return fullDateList[0] + fullDateList[1];
}

function getACDateString(f) {
  let fullDateList = f.split("/")[1];
  fullDateList = fullDateList.split(".")[0].split("_");
  return fullDateList[0] + fullDateList[1] + fullDateList[2] + fullDateList[3] + fullDateList[4];
}

function buildTeamsFromDrivers(data) {
  const drivers = data?.drivers?.list ?? [];
  const teamsFromData = Array.isArray(data?.teams?.teams) ? data.teams.teams : [];

  const byName = new Map();

  for (const t of teamsFromData) {
    const name = normalizeTeamName(t?.name);
    if (!name) continue;

    byName.set(name, {
      name,
      country: t?.country || "",
      car: t?.car || "",
      drivers: Array.isArray(t?.drivers) ? [...t.drivers] : t?.drivers ? [t.drivers] : [],
      images: [],
      memberDriverIds: [],
    });
  }

  for (const d of drivers) {
    const teamName = normalizeTeamName(d?.team);
    if (!teamName) continue;

    if (!byName.has(teamName)) {
      byName.set(teamName, {
        name: teamName,
        country: "",
        car: "",
        drivers: [],
        images: [],
        memberDriverIds: [],
      });
    }

    const entry = byName.get(teamName);

    if (d?.name && !entry.drivers.includes(d.name)) {
      entry.drivers.push(d.name);
    }

    if (d?.id && !entry.memberDriverIds.includes(d.id)) {
      entry.memberDriverIds.push(d.id);
    }

    if (!entry.car && d?.car) {
      entry.car = d.car;
    }

    if (d?.image) {
      entry.images.push(d.image);
    }
  }

  return [...byName.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((team) => ({
      ...team,
      driverCount: team.drivers.length,
      heroImage: getTeamImage(team.name),
    }));
}

function calculateScore(team) {
  let score = 10 * (team.stats?.wins || 0) + 4 * (team.stats?.podiums || 0) + (50 - team.stats?.avgPos || 0);
  return score;
}

function rankTeams(teams) {
  return [...teams]
    .sort((a, b) => {
      const scoreA = calculateScore(a);
      const scoreB = calculateScore(b);
      a.score = Math.floor(scoreA);
      b.score = Math.floor(scoreB);
      if (scoreB === scoreA) {
        if (b.stats?.wins == a.stats?.wins) {
          if (b.stats?.podiums == a.stats?.podiums) {
            if (b.stats?.avgPos == a.stats?.avgPos) {
              if (b.stats?.finishes == a.stats?.finishes) {
                return b.stats?.totalRaceLaps - a.stats?.totalRaceLaps;
              }
              return b.stats?.finishes - a.stats?.finishes;
            }
            return a.stats?.avgPos - b.stats?.avgPos;
          }
          return b.stats?.podiums - a.stats?.podiums;
        }
        return b.stats?.wins - a.stats?.wins;
      }
      return scoreB - scoreA;
    })
    .map((team, index) => ({
      ...team,
      rank: index + 1,
    }));
}

function TeamHero({ team }) {
  return (
    <Box
      sx={{
        position: "relative",
        minWidth: 72,
        width: 72,
        height: 72,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {team.heroImage ? (
        <img
          src={team.heroImage}
          alt={team.name}
          onError={useTeamImageFallback}
          style={{ width: "100%" }}
        />
      ) : (
        <Groups2Icon sx={{ fontSize: 36, color: "white" }} />
      )}
    </Box>
  );
}

function TeamListCard({ team, onClick }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        maxWidth: "82dvw"
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TeamHero team={team} />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Chip
                  size="small"
                  sx={{backgroundColor:team.rank === 1 ? "#b68b00" : team.rank === 2 ? "#807b6a" : team.rank === 3 ? "#8b3c00" : "primary"}}
                  label={`#${team.rank}`}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 900, minWidth: 0 }}
                  noWrap
                >
                  {team.name}
                </Typography>
              </Stack>
              <ChevronRightIcon color="action" />
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
              <Chip
                size="small"
                icon={<Groups2Icon />}
                label={`${team.driverCount} driver${team.driverCount === 1 ? "" : "s"}`}
              />
              <Chip
                size="small"
                icon={<MilitaryTechIcon />}
                label={`${team.stats?.wins || 0} win${(team.stats?.wins || 0) === 1 ? "" : "s"}`}
              />
              {team.country ? (
                <Chip size="small" icon={<PublicIcon />} label={team.country} variant="outlined" />
              ) : null}
            </Stack>

            <Typography variant="body2" color="text.secondary" noWrap>
              {`${team.stats?.podiums || 0} pod • ${
                Number.isFinite(team.stats?.avgPos) ? `P${team.stats.avgPos.toFixed(2)} avg` : "— avg"
              } • ${team.stats?.finishes || 0} races • ${team.stats?.totalRaceLaps || 0} laps`}
            </Typography>

            {team.car ? (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.35 }}>
                {team.car}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function TeamDesktopCard({ team, onClick }) {
  return (
    <Card
      sx={{
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
        cursor: "pointer",
        transition: "transform .15s ease, box-shadow .15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 6,
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          height: 150,
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Chip
          label={`#${team.rank}`}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            fontWeight: 800,
            backgroundColor: team.rank === 1 ? "#b68b00" : team.rank === 2 ? "#807b6a" : team.rank === 3 ? "#8b3c00" : "primary"
          }}
        />

        {team.heroImage ? (
          <img
            src={team.heroImage}
            alt={team.name}
            onError={useTeamImageFallback}
            style={{ width: "140px" }}
          />
        ) : (
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: "white",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              px: 2,
              textAlign: "center",
            }}
          >
            {team.name}
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {team.name}
          </Typography>
          <ChevronRightIcon color="action" />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 1.5 }}>
          <Chip
            size="small"
            icon={<MilitaryTechIcon />}
            label={`${team.stats?.wins || 0} win${(team.stats?.wins || 0) === 1 ? "" : "s"}`}
          />
          <Chip
            size="small"
            icon={<EmojiEventsIcon />}
            label={`${team.stats?.podiums || 0} podium${(team.stats?.podiums || 0) === 1 ? "" : "s"}`}
          />
          {team.country ? (
            <Chip size="small" icon={<PublicIcon />} label={team.country} variant="outlined" />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 1.5 }}>
          <Chip
            size="small"
            icon={<Groups2Icon />}
            label={`${team.driverCount} driver${team.driverCount === 1 ? "" : "s"}`}
          />
          <Chip
            size="small"
            icon={<EmojiEventsIcon />}
            label={`${team.score || 0} score`}
          />
          {team.country ? (
            <Chip size="small" icon={<PublicIcon />} label={team.country} variant="outlined" />
          ) : null}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          <SportsScoreIcon sx={{ fontSize: 16, mr: 0.75, verticalAlign: "text-bottom" }} />
          {Number.isFinite(team.stats?.avgPos) ? `Average finish: P${team.stats.avgPos.toFixed(2)}` : "Average finish: —"}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.75, verticalAlign: "text-bottom" }} />
          {`${team.stats?.finishes || 0} races • ${team.stats?.totalRaceLaps || 0} race laps`}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          PB: {Number.isFinite(team.stats?.bestLap) ? msToLap(team.stats.bestLap) : "—"}
        </Typography>

        {team.car ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <DirectionsCarFilledIcon sx={{ fontSize: 16, mr: 0.75, verticalAlign: "text-bottom" }} />
            {team.car}
          </Typography>
        ) : null}
      </Box>
    </Card>
  );
}

function TeamCardSkeleton() {
  return (
    <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ height: 150, p: 2 }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
      <Box sx={{ p: 2 }}>
        <Skeleton width="60%" height={32} />
        <Skeleton width="90%" />
        <Skeleton width="75%" />
        <Skeleton width="55%" />
      </Box>
    </Card>
  );
}

/**
 * Teams page
 * Props: { data }
 */
export default function TeamsPage({ data }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rankedTeams, setRankedTeams] = useState([]);

  const intro =
    data?.teams?.intro ??
    data?.teamsIntro ??
    "Team lineups, drivers, and constructors.";

  const baseTeams = useMemo(() => buildTeamsFromDrivers(data), [data]);
  const drivers = data?.drivers?.list ?? [];

  const totalDrivers = useMemo(
    () => baseTeams.reduce((acc, team) => acc + (team.driverCount || 0), 0),
    [baseTeams]
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);

      const toUrl = (f) =>
        process.env.PUBLIC_URL
          ? `${process.env.PUBLIC_URL}${f.startsWith("/") ? "" : "/"}${f}`
          : `${f.startsWith("/") ? "" : "/"}${f}`;

      const [accList, acList] = await Promise.all([
        Promise.all(
          [...ACC_FILES]
            .sort((a, b) => getACCDateString(a) - getACCDateString(b))
            .map((f) => fetchJSON(toUrl(f)))
        ),
        Promise.all(
          [...AC_FILES]
            .sort((a, b) => getACDateString(a) - getACDateString(b))
            .map((f) => fetchJSON(toUrl(f)))
        ),
      ]);

      const accJsons = accList.filter(Boolean);
      const acJsons = acList.filter(Boolean);

      const driverRowsMap = {};

      for (const d of drivers) {
        const dAcc = accJsons.map((j) => parseACC(j, d.id)).filter(Boolean);
        const dAc = acJsons.map((j) => parseAC(j, d.id)).filter(Boolean);
        driverRowsMap[String(d.id)] = [...dAc, ...dAcc];
      }

      const teamsWithStats = baseTeams.map((team) => {
        const teamRows = [];

        for (const driverId of team.memberDriverIds || []) {
          const rows = driverRowsMap[String(driverId)] || [];
          teamRows.push(...rows);
        }

        const raceStats = computeRaceStatsFromRows(teamRows);
        const bestLap = bestOfRows(teamRows.filter((r) => r.session === "Race"));

        return {
          ...team,
          stats: {
            ...raceStats,
            bestLap,
          },
        };
      });

      const ranked = rankTeams(teamsWithStats);

      if (!alive) return;

      setRankedTeams(ranked);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [baseTeams, drivers]);

  const handleOpenTeam = (teamName) => {
    navigate(`/team/${teamName}`);
  };

  return (
    <PageShell title="Teams" bannerImage="team.png">
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            mb: 2,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.07))",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.75 }}>
            Teams
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {intro}
          </Typography>

          <Stack direction={{ md:"row" }} spacing={1} flexWrap="wrap">
            <Chip icon={<EmojiEventsIcon />} label={`${baseTeams.length} team${baseTeams.length === 1 ? "" : "s"}`} sx={{mb:1}} />
            <Chip icon={<Groups2Icon />} label={`${totalDrivers} driver${totalDrivers === 1 ? "" : "s"}`} variant="outlined" sx={{mb:1}} />
            <Chip icon={<MilitaryTechIcon />} label="Ranked by member results" variant="outlined" />
          </Stack>
        </Paper>
        <ResultHistory
          data={data}
          sim="ACC"
          initialMode="teams"
          lockMode
          title="Team Points Graph"
        />

        {/* Mobile-first list */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {loading ? (
            <List disablePadding sx={{ display: "grid", gap: 1.25 }}>
              {[...Array(6)].map((_, i) => (
                <ListItem key={i} disablePadding>
                  <Box sx={{ width: "100%" }}>
                    <Card sx={{ borderRadius: 3, p: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="rectangular" width={72} height={72} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="50%" />
                          <Skeleton width="80%" />
                          <Skeleton width="65%" />
                        </Box>
                      </Stack>
                    </Card>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : rankedTeams.length > 0 ? (
            <List disablePadding sx={{ display: "grid", gap: 1.25 }}>
              {rankedTeams.map((team) => (
                <ListItem key={team.name} disablePadding>
                  <Box sx={{ width: "100%" }}>
                    <TeamListCard
                      team={team}
                      onClick={() => handleOpenTeam(team.name)}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="text.secondary">No teams found.</Typography>
            </Paper>
          )}
        </Box>

        {/* Desktop grid */}
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          {loading ? (
            <Grid container spacing={2.5}>
              {[...Array(6)].map((_, i) => (
                <Grid key={i} item xs={12} sm={6} lg={4}>
                  <TeamCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : rankedTeams.length > 0 ? (
            <Grid container spacing={2.5}>
              {rankedTeams.map((team) => (
                <Grid key={team.name} item xs={12} sm={6} lg={4}>
                  <TeamDesktopCard
                    team={team}
                    onClick={() => handleOpenTeam(team.name)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography color="text.secondary">No teams found.</Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </PageShell>
  );
}
